import { useEffect, useState } from "react";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useDotLegalMutation, useDotLegalQuery } from "@common/hooks/dotLegalReactQuery";
import { post, put } from "@common/api/apiShared";
import { useTranslation } from "@localization/useTranslation";
import { RiskTreatmentType } from "../../riskTreatmentType";
import { isTerminalMitigationStatus } from "../../riskMitigationStatus";
import { getCompletedMitigationStaleText, getDriftWarningText } from "../riskDriftWarning";
import { TreatmentDialogProps, MitigationPlanSaveModel, RiskMitigationPlanViewModel } from "./TreatmentDialogs.types";
import { toLevelOptions, useMitigationPlanForm } from "./useMitigationPlanForm";
import { resolveControls } from "../../../policy/policySections/PolicySections.utils";
import { formatExposureValue } from "../../riskExposure/formatExposureValue";
import { useUrlProvider } from "../../../useUrlProvider";

export function useMitigationPlanDialog(props: TreatmentDialogProps) {
    const { translateString, translateDate, translateNumber } = useTranslation();
    const snackbar = useDotLegalSnackbar();
    const { getTaskManagementTaskDialogUrl } = useUrlProvider();

    const { scenario } = props;

    const planForm = useMitigationPlanForm();

    // The current assessment's exposure for the "Current: ..." subtitle — already frame-gated in the backend.
    const currentExposureText = formatExposureValue(
        scenario.exposureValue,
        planForm.exposureMode,
        planForm.exposureCurrencyCode,
        planForm.exposureScaleUpperBound,
        translateNumber
    );

    // A drifted mitigation plan can be re-based onto the current revision via the reconfirm endpoint, rather
    // than being re-planned field by field.
    const isDrifted = scenario.treatmentType === RiskTreatmentType.Mitigate && scenario.isTreatmentDrifted;
    // Mirror the assessments-overview tooltip: lead with the anchor-vs-current facts so the dialog spells out
    // exactly what drifted, not just the generic "review the plan" action sentence.
    const driftWarningText = getDriftWarningText(scenario, translateString, translateDate);

    // The scenario row's id is the RiskAssessment head id. The plan GET 404s for a non-mitigated assessment,
    // which apiShared turns into a hard redirect, so only fetch when there is a plan to load: either a specific
    // plan addressed by id (the mitigation overview opens terminal plans this way) or this scenario's current
    // mitigation treatment.
    const hasPlan = props.planId !== undefined || scenario.treatmentType === RiskTreatmentType.Mitigate;
    const planUrl = props.planId ? `/risk-assessments/${scenario.id}/mitigation-plan/${props.planId}` : `/risk-assessments/${scenario.id}/mitigation-plan`;
    const prefillQuery = useDotLegalQuery<RiskMitigationPlanViewModel>(planUrl, {
        enabled: hasPlan,
    });

    const isLoading = hasPlan && prefillQuery.isLoading;
    const prefill = prefillQuery.data;

    // Only present once the plan has materialised a board task (tracking on, task not yet deleted), so the
    // dialog can link straight to it.
    const linkedTaskUrl = prefill?.linkedTaskId ? getTaskManagementTaskDialogUrl(prefill.linkedTaskId) : null;

    // A completed plan whose risk was reassessed since is re-mitigated in place: the dialog opens read-only on
    // the completed plan, and "create new mitigation" flips into draft mode so Save proposes a fresh plan
    // (which supersedes the completed one and becomes the current treatment) instead of editing the closed plan.
    const [isCreatingNew, setIsCreatingNew] = useState(false);

    const isEditingPlan = prefill !== undefined && !isCreatingNew;
    // A terminal plan (Completed/Promoted/Cancelled) is closed: the dialog shows it for viewing only, unless we
    // have flipped into create-new draft mode off the back of a stale completed plan.
    const isReadOnly = prefill !== undefined && isTerminalMitigationStatus(prefill.status) && !isCreatingNew;
    const showStaleMitigationPrompt = scenario.isCompletedMitigationStale && !isCreatingNew;
    const staleWarningText = getCompletedMitigationStaleText(scenario, translateString, translateDate);

    const { setForm } = planForm;
    useEffect(() => {
        if (!prefill) return;

        setForm({
            name: prefill.name,
            description: prefill.description,
            responsibleId: prefill.responsibleId,
            deadline: prefill.deadline ? new Date(prefill.deadline) : null,
            expectedConsequence: prefill.expectedConsequence.toString(),
            expectedProbability: prefill.expectedProbability.toString(),
            estimatedCost: prefill.estimatedCost?.toString() ?? "",
            expectedExposureValue: prefill.expectedExposureValue?.toString() ?? "",
            securityMeasureIds: prefill.securityMeasureIds,
            frameworkControlIds: prefill.frameworkControlIds,
        });
    }, [prefill, setForm]);

    const consequenceOptions = toLevelOptions(scenario.consequenceLevels);
    const probabilityOptions = toLevelOptions(scenario.probabilityLevels);

    const { consequence, probability, consequenceLabel, probabilityLabel } = scenario;
    const currentRisk = consequence !== null && probability !== null ? { consequence, consequenceLabel, probability, probabilityLabel } : null;

    // A control already on the risk assessment head is standing coverage that graduates onto the head on
    // promotion, so it is not selectable for the plan (the backend also strips it). Any that are somehow already
    // on this plan are kept so their chips still render and can be removed.
    const availableControls = planForm.allControls.filter(
        (control) => !scenario.linkedControlIds.includes(control.id) || planForm.frameworkControlIds.includes(control.id)
    );

    // The head's standing coverage, shown read-only above the plan's own controls so deciding what this round
    // should add happens with what already stands in view. Resolved against the unfiltered set — the filtered
    // availableControls has exactly these stripped out.
    const existingCoverageControls = resolveControls(scenario.linkedControlIds, planForm.allControls);

    const savePlanMutation = useDotLegalMutation((model: MitigationPlanSaveModel) => {
        const url = `/risk-assessments/${scenario.id}/mitigation-plan`;
        return isEditingPlan ? put(url, model) : post(url, model);
    });

    const reconfirmMutation = useDotLegalMutation(() => post(`/risk-assessments/${scenario.id}/treatment/reconfirm`, {}));

    const onSave = () => {
        if (isReadOnly) return;

        if (planForm.validation.anyHasErrors) {
            planForm.validation.setShowErrors(true);
            return;
        }

        savePlanMutation.mutateAsync(planForm.toSavePayload(), {
            onSuccess: () => {
                snackbar.show(translateString("treatmentSaved"));
                props.onSaved();
            },
        });
    };

    const reconfirm = () =>
        reconfirmMutation.mutateAsync(undefined, {
            onSuccess: () => {
                snackbar.show(translateString("treatmentReconfirmed"));
                props.onSaved();
            },
        });

    // The completed plan's substance carries over as the starting point for its successor; Save then proposes
    // it as a new plan. The user re-scores the expected residual against the now-current risk shown in the form.
    const createNewMitigation = () => setIsCreatingNew(true);

    const inProgress = savePlanMutation.isLoading || reconfirmMutation.isLoading;

    return {
        planForm,
        isLoading,
        isReadOnly,
        isDrifted,
        driftWarningText,
        reconfirm,
        showStaleMitigationPrompt,
        staleWarningText,
        createNewMitigation,
        inProgress,
        onSave,
        linkedTaskUrl,
        currentRisk,
        consequenceOptions,
        probabilityOptions,
        currentExposureText,
        availableControls,
        existingCoverageControls,
    };
}
